import api from '@/Services/api';
import careService from '@/Services/careService';
import careHelpfulFunctions from '@/Application/careHelpfulFunctions.jsx';
import { appSettings } from '@/Shared/appSettings';
import _ from 'lodash';
import {
    SCOPE_LIBRARIES,
    attachScopeLibraries,
    detachScopeLibraries,
    getScopeLibraries,
    getControlScope,
    compileLibraryFactory,
    compileUserControlScopeScript,
    isScopeScriptEmpty,
    createInstantiateLibrary,
    cacheKey,
    loadInProgressKey,
    normalizePath,
    parseScopeItemsFromResponse,
} from '@/Shared/scopeLibraryCore';

export {
    SCOPE_LIBRARIES,
    attachScopeLibraries,
    detachScopeLibraries,
    getScopeLibraries,
    getControlScope,
    compileLibraryFactory,
    compileUserControlScopeScript,
    isScopeScriptEmpty,
};

let documentCacheRef = null;

export function bindScopeLibraryDocumentCache(getter) {
    documentCacheRef = getter;
}

function getDocumentCache() {
    if (!documentCacheRef)
        throw new Error('scopeLibrary document cache not bound');
    return documentCacheRef();
}

function getCustomerId(headers) {
    if (headers?.customerid)
        return String(headers.customerid);
    if (headers?.CustomerID)
        return String(headers.CustomerID);

    const tokenCustomerId = appSettings.careTokenService?.CustomerID?.();
    if (tokenCustomerId)
        return String(tokenCustomerId);

    return 'unknown';
}

async function fetchUserControlDocument(path) {
    const normalizedPath = normalizePath(path);
    let apiRequest = {
        method: 'GET',
        url: `Document/UserControl/${normalizedPath}`,
        doNotUseWebsocket: true,
        flatten: false,
        cache: true,
    };

    let response = await api.apiRequest(apiRequest);
    let payload = response.data?.Result || response.data;

    if (!payload) {
        apiRequest.url = `Document/UserControl/public/${normalizedPath}`;
        response = await api.apiRequest(apiRequest);
        payload = response.data?.Result || response.data;
    }

    if (!payload)
        throw new Error(`Scope library not found: ${normalizedPath}`);

    return { payload, headers: response.headers || {} };
}

const instantiateLibrary = createInstantiateLibrary({
    load: (path) => scopeLibrary.load(path),
    api,
    care: careService,
    util: careHelpfulFunctions,
    _,
});

export const scopeLibrary = {
    async load(path) {
        const normalizedPath = normalizePath(path);
        if (!normalizedPath)
            throw new Error('Scope library path is required');

        const documentCache = getDocumentCache();
        if (!documentCache.scopeLibraries)
            documentCache.scopeLibraries = {};

        const { payload, headers } = await fetchUserControlDocument(normalizedPath);
        const customerId = getCustomerId(headers);
        const key = cacheKey(customerId, normalizedPath);

        if (documentCache.scopeLibraries[key])
            return documentCache.scopeLibraries[key];

        const inProgressKey = loadInProgressKey(key);
        if (documentCache.loadInProgress[inProgressKey])
            return await documentCache.loadInProgress[inProgressKey];

        const loadPromise = (async () => {
            const parsed = parseScopeItemsFromResponse(payload);
            if (!parsed)
                throw new Error(`Scope library has no scope data: ${normalizedPath}`);

            const includeHandleNames = (parsed.scopeIncludes || []).map((inc) => inc.As);
            const factory = !isScopeScriptEmpty(parsed.scopeScript)
                ? compileLibraryFactory(parsed.scopeScript, includeHandleNames)
                : () => ({});

            const cachedEntry = {
                path: normalizedPath,
                scopeScript: parsed.scopeScript,
                scopePublic: parsed.scopePublic,
                scopeIncludes: parsed.scopeIncludes || [],
                factory,
                headers,
            };

            documentCache.scopeLibraries[key] = cachedEntry;
            return cachedEntry;
        })();

        documentCache.loadInProgress[inProgressKey] = loadPromise;

        try {
            return await loadPromise;
        } finally {
            delete documentCache.loadInProgress[inProgressKey];
        }
    },

    instantiateLibrary,

    async resolveForUserControl(items) {
        const flatHandles = {};
        const rootHandles = {};

        for (const inc of items?.ScopeIncludes || []) {
            if (inc.As in flatHandles)
                throw new Error(`Duplicate scope include handle: ${inc.As}`);

            const cached = await scopeLibrary.load(inc.Path);
            const instance = await instantiateLibrary(cached, [], flatHandles);
            rootHandles[inc.As] = instance;
            flatHandles[inc.As] = instance;
        }

        return { flatHandles, rootHandles };
    },

    clearCache() {
        const documentCache = getDocumentCache();
        documentCache.scopeLibraries = {};

        for (const key of Object.keys(documentCache.loadInProgress || {})) {
            if (key.startsWith('scopeLibrary:'))
                delete documentCache.loadInProgress[key];
        }
    },
};
